import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import BrandHeader from "../components/BrandHeader";
import ProgressStepper, { RESPONDENT_STEPS } from "../components/ProgressStepper";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { toast } from "sonner";
import { CheckCircle2, ShieldCheck, PlayCircle, Circle } from "lucide-react";

const BAND_COLORS = {
  Normal: "bg-primary/10 text-primary border-primary/30",
  Mild: "bg-secondary/30 text-primary border-secondary",
  Moderate: "bg-[hsl(var(--chart-4)/0.25)] text-foreground border-[hsl(var(--chart-4))]",
  High: "bg-destructive/10 text-destructive border-destructive/40",
};

const SECTION_LABEL = {
  MENTAL_HEALTH: "Mental Health",
  BRAIN_HEALTH: "Brain Health",
  ADDICTIONS: "Addictions",
  COGNITIVE_HEALTH: "Brain Health",
  INTOXICATION: "Addictions",
  NEURODEGENERATIVE: "Brain Health",
  SUBSTANCE_ABUSE: "Addictions",
};

const prettySection = (s) => SECTION_LABEL[s]
  || (s || "OTHER").replace(/_/g, " ").replace(/\w\S*/g, w => w[0] + w.slice(1).toLowerCase());

export default function Results() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null); // completeness view

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    Promise.all([
      api.get("/assessments/my/summary"),
      api.get("/assessments/status").catch(() => ({ data: null })),
    ]).then(([sumRes, statusRes]) => {
      setData(sumRes.data);
      setStatus(statusRes.data);
    }).catch(() => toast.error("Could not load results"));
  }, [user, loading, nav]);

  const withdraw = async () => {
    if (!confirm("Withdraw consent? Your future data collection will stop.")) return;
    try {
      await api.post("/consent/withdraw", { reason: "user_action" });
      toast.success("Consent withdrawn");
      await logout();
      nav("/");
    } catch { toast.error("Withdrawal failed"); }
  };

  if (!data) {
    return (
      <div className="min-h-screen bg-background">
        <BrandHeader />
        <div className="mano-container-narrow py-20 text-muted-foreground">Loading summary…</div>
      </div>
    );
  }

  const sections = Object.keys(data.grouped || {});
  const showResults = data.show_results_to_respondent;

  return (
    <div className="min-h-screen bg-background">
      <BrandHeader />
      <main className="mano-container-narrow py-12">
        <ProgressStepper steps={RESPONDENT_STEPS} current="results" />

        <div className="mb-10">
          <div className="inline-flex items-center gap-2 rounded-full bg-secondary/30 px-3 py-1 text-xs text-primary">
            <CheckCircle2 size={14} /> Assessment complete
          </div>
          <h1 className="font-display font-semibold text-3xl sm:text-4xl tracking-tight mt-4">Thank you.</h1>
          <p className="text-muted-foreground mt-3 leading-relaxed">
            Your responses have been securely recorded. A clinical review may be initiated based on your results.
          </p>
        </div>

        {!showResults ? (
          <Card className="mano-card">
            <CardContent className="p-8 text-center">
              <ShieldCheck className="mx-auto text-primary" size={28} />
              <p className="mt-4 text-muted-foreground">
                Your results are being reviewed by an independent clinical panel. You will be notified when the review is complete.
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {status && status.total_count > 0 && (
              <Card className="mano-card mb-6" data-testid="results-completeness">
                <CardContent className="p-5 md:p-6">
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Completeness</div>
                      <div className="font-display text-lg mt-1"
                           data-testid="results-completeness-count">
                        {status.filled_count} of {status.total_count} questionnaires filled
                      </div>
                    </div>
                    <div className="w-40 h-1.5 rounded-full bg-muted overflow-hidden">
                      <div className="h-1.5 rounded-full bg-primary transition-[width]"
                           style={{ width: `${status.total_count ? Math.round((status.filled_count/status.total_count)*100) : 0}%` }} />
                    </div>
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {status.instruments.map((i) => (
                      <div key={i.instrument_id}
                           className={"flex items-center justify-between gap-3 rounded-lg border p-3 text-sm " +
                                       (i.completed ? "border-primary/40 bg-primary/5" : "border-border/60 bg-muted/20")}
                           data-testid={`results-status-${i.instrument_id}`}>
                        <div className="flex items-center gap-2 min-w-0">
                          {i.completed
                            ? <CheckCircle2 size={14} className="text-primary shrink-0" />
                            : <Circle size={14} className="text-muted-foreground shrink-0" />}
                          <div className="min-w-0">
                            <div className="font-medium truncate">{i.title}</div>
                            <div className="text-[10px] text-muted-foreground">
                              {i.completed
                                ? `${i.band || "Recorded"}${i.score !== null && i.score !== undefined ? ` · Score ${i.score}` : ""}`
                                : "Not started"}
                            </div>
                          </div>
                        </div>
                        {!i.completed && (
                          <Button size="sm" variant="outline"
                                  onClick={() => nav(`/q/${i.instrument_id}`)}
                                  className="rounded-full shrink-0 text-xs"
                                  data-testid={`results-start-${i.instrument_id}`}>
                            <PlayCircle size={12} /> Start
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            {sections.length === 0 ? (
              <Card className="mano-card">
                <CardContent className="p-8 text-muted-foreground" data-testid="results-empty">
                  No questionnaire responses on record yet.
                </CardContent>
              </Card>
            ) : (
          <div className="space-y-8" data-testid="results-list">
            {sections.map((section) => (
              <div key={section}>
                <h2 className="font-display text-xl sm:text-2xl tracking-tight mb-4">{prettySection(section)}</h2>
                <div className="grid gap-4">
                  {data.grouped[section].map((r) => (
                    <Card key={r.instrument_id} className="mano-card">
                      <CardContent className="p-6 space-y-4">
                        <div className="flex flex-wrap items-center justify-between gap-4">
                          <div>
                            <div className="text-xs text-muted-foreground uppercase tracking-wider">
                              {r.instrument_id}
                            </div>
                            <div className="font-display font-semibold text-lg mt-1">{r.title}</div>
                            <div className="text-sm text-muted-foreground mt-1">{r.label}</div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Badge className={"border " + (BAND_COLORS[r.band] || "")} data-testid={`result-band-${r.instrument_id}`}>
                              {r.band}
                            </Badge>
                            <div className="text-right">
                              <div className="text-xs text-muted-foreground">Score</div>
                              <div className="font-display text-2xl leading-none" data-testid={`result-total-${r.instrument_id}`}>{r.total}</div>
                            </div>
                          </div>
                        </div>
                        {r.domain_scores && r.domain_scores.length > 0 && (
                          <div
                            className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-2 border-t border-border/50"
                            data-testid={`result-domains-${r.instrument_id}`}
                          >
                            {r.domain_scores.map((d) => {
                              const pct = d.max ? (d.score / d.max) : 0;
                              const tone =
                                d.max == null ? "border-border/60 bg-muted/40"
                                : pct >= 0.85 ? "border-primary/30 bg-primary/5"
                                : pct >= 0.6 ? "border-secondary bg-secondary/15"
                                : "border-destructive/40 bg-destructive/5";
                              return (
                                <div
                                  key={d.key}
                                  className={"rounded-lg border p-2 " + tone}
                                  data-testid={`result-domain-${r.instrument_id}-${d.key}`}
                                >
                                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                                    {d.label}
                                  </div>
                                  <div className="font-display text-base leading-tight mt-0.5">
                                    {d.score}
                                    {d.max != null && <span className="text-xs text-muted-foreground">/{d.max}</span>}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
          )}
          </>
        )}

        <div className="mt-10 border-t border-border/60 pt-8 flex flex-wrap items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground max-w-md">
            These results are a screening aid, not a diagnosis. If you have any concerns about your wellbeing, please contact a licensed clinician.
          </p>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => nav("/insights")} className="rounded-full px-6" data-testid="results-insights-btn">
              View my insights
            </Button>
            <Button variant="outline" onClick={() => nav("/")} className="rounded-full px-6" data-testid="results-home-btn">
              Back to home
            </Button>
            <Button variant="ghost" onClick={withdraw} className="rounded-full px-6" data-testid="results-withdraw-btn">
              Withdraw consent
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
